import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

function AuthenticatedNavbar({ activeTab, menuItems, toggleMobileMenu, isMobileMenuOpen, sideNavCollapsed = false }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const currentItem = Array.isArray(menuItems)
        ? menuItems.find(item => item.id === activeTab)
        : null;
    const currentLabel = currentItem?.label || 'Workspace';
    const avatarLetter = (user?.email?.charAt(0) || 'A').toUpperCase();

    return (
        <>
            <header
                className={`bg-[#060b18]/90 backdrop-blur-md border-b border-slate-800/60 px-4 sm:px-6 fixed top-0 right-0 transition-all duration-300 flex items-center h-[72px] ${
                    isMobileMenuOpen ? 'left-[104px] z-30' : 'left-0 z-50'
                } md:z-50 ${sideNavCollapsed ? 'md:left-[72px]' : 'md:left-[104px]'}`}
            >
                <div className="flex items-center justify-between w-full">
                    {/* Left: hamburger (mobile) + page title (desktop) */}
                    <div className="flex items-center gap-3 min-w-0">
                        <button
                            onClick={toggleMobileMenu}
                            className="md:hidden text-slate-400 hover:text-white transition-colors duration-200"
                            aria-label="Toggle menu"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>

                        <h1 className="hidden md:block text-base font-semibold text-white tracking-tight truncate">
                            {currentLabel}
                        </h1>
                    </div>

                    {/* Right: user chip */}
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full hover:bg-slate-800/60 transition-colors duration-150 group"
                            aria-haspopup="true"
                            aria-expanded={isDropdownOpen}
                        >
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold text-xs">
                                {avatarLetter}
                            </div>
                            <svg
                                className={`w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 transition-all duration-200 ${
                                    isDropdownOpen ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-60 bg-[#0a1224] border border-slate-800 rounded-lg shadow-xl shadow-black/40 overflow-hidden z-[60] animate-slideDown">
                                <div className="px-3 py-2.5 border-b border-slate-800/80">
                                    <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-0.5">Signed in</p>
                                    <p className="text-sm text-slate-200 truncate">
                                        {user?.email || 'operator@unknown'}
                                    </p>
                                </div>

                                <div className="py-1">
                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-slate-400 hover:bg-slate-800/60 hover:text-red-400 transition-colors duration-150"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                        </svg>
                                        <span className="text-sm font-medium">Sign out</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <style jsx>{`
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-4px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-slideDown {
                    animation: slideDown 0.16s ease-out forwards;
                }
            `}</style>
        </>
    );
}

export default AuthenticatedNavbar;
